import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGuest } from '../context/GuestContext';
import { toast } from 'sonner';
import axios from 'axios';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import CandidateSelector from '../components/CandidateSelector';
import OnboardingModal from '../components/OnboardingModal';
import { FlipValueLogoBox } from '../components/FlipValueLogo';
import { 
  Camera, Upload, History, LogOut, Moon, Sun, Zap, X, 
  Loader2, Image as ImageIcon, Trash2, ChevronRight, RefreshCw, Package, PackageOpen, Search, Maximize2, Minimize2, AlertTriangle, ArrowLeft, ChevronDown, Calendar, Calculator
} from 'lucide-react';
import { 
  compressImage, 
  processImageFile, 
  captureFromVideo, 
  getCameraConstraints,
  formatFileSize,
  getBase64Size,
  isLowMemoryDevice 
} from '../utils/imageOptimizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature flag for scan functionality - set to false for production launch
// SCAN DISABLED - Set to false to save Emergent LLM credits
// Text search still works (uses eBay API only, no credits)
const ENABLE_SCAN = false; // process.env.REACT_APP_ENABLE_SCAN === 'true';

export default function DashboardPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [cameraMode, setCameraMode] = useState(false);
  const [capturedImages, setCapturedImages] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState('New'); // Default to New
  const [searchCondition, setSearchCondition] = useState('New'); // For manual search - default New
  const [previewImage, setPreviewImage] = useState(null); // For maximizing thumbnails
  const [processingImage, setProcessingImage] = useState(false);
  const [imageProcessProgress, setImageProcessProgress] = useState(0);
  const [showCardSuggestion, setShowCardSuggestion] = useState(false); // Suggest Used for cards
  const [showConfirmResults, setShowConfirmResults] = useState(false); // Confirm results popup
  const [searchResults, setSearchResults] = useState(null); // Search results for confirmation
  
  // New state for visual identification flow
  const [identificationStep, setIdentificationStep] = useState('capture'); // 'capture' | 'identifying' | 'select' | 'pricing'
  const [identificationResult, setIdentificationResult] = useState(null);
  const [confirmingSelection, setConfirmingSelection] = useState(false);
  const [collapsedDates, setCollapsedDates] = useState({}); // Track collapsed date groups
  
  // Helper to format date for grouping
  const formatDateGroup = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Group scans by date
  const groupScansByDate = (scanList) => {
    const groups = {};
    scanList.forEach(scan => {
      const dateKey = formatDateGroup(scan.created_at || scan.timestamp || new Date());
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(scan);
    });
    return groups;
  };
  
  // Initialize collapsed state - collapse all except Today
  useEffect(() => {
    if (scans.length > 0) {
      const groups = groupScansByDate(scans);
      const initialCollapsed = {};
      Object.keys(groups).forEach(key => {
        if (key !== 'Today') initialCollapsed[key] = true;
      });
      setCollapsedDates(initialCollapsed);
    }
  }, [scans.length]);
  
  // Check if search query is for trading cards
  const isCardSearch = (query) => {
    const cardKeywords = ['card', 'pokemon', 'pokémon', 'yugioh', 'yu-gi-oh', 'magic the gathering', 'mtg', 'topps', 'panini', 'psa', 'bgs', 'cgc', 'sgc', 'charizard', 'pikachu', 'baseball card', 'football card', 'basketball card'];
    const lowerQuery = query.toLowerCase();
    return cardKeywords.some(kw => lowerQuery.includes(kw));
  };
  
  // Execute search and show results for confirmation
  const executeSearch = async () => {
    // Check guest limit BEFORE making the API call (counts toward limit)
    if (!user && !checkActionLimit()) {
      return;
    }
    
    // Record action immediately for guests (before API call)
    if (!user) {
      recordAction();
    }
    
    setSearching(true);
    setShowCardSuggestion(false);
    try {
      const response = await axios.post(`${API}/search`, { 
        query: searchQuery.trim(),
        condition: searchCondition
      });
      
      // Show results for confirmation
      setSearchResults(response.data);
      setShowConfirmResults(true);
      
      // Add to local history for guests
      if (!user) {
        addToLocalHistory(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };
  
  // Confirm and navigate to results
  const confirmResults = async () => {
    if (searchResults) {
      // Track search usage when user confirms results
      await trackSearchUsage();
      // Store search data in sessionStorage for guests (they don't have saved searches in DB)
      sessionStorage.setItem(`search_${searchResults.id}`, JSON.stringify(searchResults));
      navigate(`/search/${searchResults.id}`);
    }
    setShowConfirmResults(false);
  };
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null); // For native camera capture
  
  const navigate = useNavigate();
  const { user, logout, refreshUser, token, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { checkActionLimit, recordAction, addToLocalHistory, localHistory } = useGuest();
  
  const isGuest = !user;
  const isFreeOrBasic = !user || !user.subscription || ['free', 'basic'].includes(user.subscription?.tier);

  // Track search usage
  const trackSearchUsage = async () => {
    if (isGuest) {
      recordAction();
    } else if (token) {
      try {
        await axios.post(
          `${API}/usage/track?usage_type=scans_used`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to track search usage:', error);
      }
    }
  };

  useEffect(() => {
    // Clear scans when user changes to prevent showing wrong user's data
    setScans([]);
    setLoading(true);
    
    // Only fetch scans after auth is ready (authLoading is false) AND we have a token
    if (!authLoading) {
      if (user && token) {
        // Logged in user with valid token - fetch their scans from API
        fetchScans();
      } else {
        // Guest or no token yet - don't fetch, use localHistory instead
        setLoading(false);
      }
    }
  }, [user, authLoading, token]);

  const fetchScans = async () => {
    // Double-check we have a token before making the request
    if (!token) {
      console.log('No token available, skipping scans fetch');
      setScans([]);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/scans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScans(response.data);
    } catch (error) {
      console.error('Failed to fetch scans:', error);
      setScans([]); // Clear scans on error
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Camera not supported on this device/browser. Please upload an image instead.');
        return;
      }
      
      setCameraMode(true);
      
      // Small delay to ensure video element is mounted
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use optimized camera constraints for the device
      const constraints = getCameraConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().then(resolve).catch(resolve);
          };
        });
      }
      
      // Show tip for low memory devices
      if (isLowMemoryDevice()) {
        toast.info('Tip: For best results, close other apps before searching', { duration: 3000 });
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraMode(false);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found. Please upload an image instead.');
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera is in use by another app. Please close it and try again.');
      } else if (error.message?.includes('memory') || error.name === 'AbortError') {
        toast.error('Low memory detected. Try closing other tabs/apps and retry.', { duration: 5000 });
      } else {
        toast.error('Could not access camera. Please try uploading an image.');
      }
    }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraMode(false);
  }, []);

  const capturePhoto = async () => {
    if (!videoRef.current) return;
    
    try {
      setProcessingImage(true);
      setImageProcessProgress(30);
      
      // Use optimized capture with compression
      const imageData = await captureFromVideo(videoRef.current);
      
      setImageProcessProgress(80);
      
      // Check compressed size
      const sizeBytes = getBase64Size(imageData);
      const sizeMB = sizeBytes / (1024 * 1024);
      
      if (sizeMB > 2) {
        // Further compress if still too large
        const recompressed = await compressImage(imageData, { quality: 0.6 });
        setCapturedImages(prev => [...prev, recompressed]);
      } else {
        setCapturedImages(prev => [...prev, imageData]);
      }
      
      setImageProcessProgress(100);
      toast.success(`Photo captured (${formatFileSize(sizeBytes)})`);
    } catch (error) {
      console.error('Capture error:', error);
      if (error.message?.includes('memory')) {
        toast.error('Low memory. Try closing other apps and retry.', { duration: 5000 });
      } else {
        toast.error('Failed to capture photo. Please try again.');
      }
    } finally {
      setProcessingImage(false);
      setImageProcessProgress(0);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    setProcessingImage(true);
    setImageProcessProgress(0);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (!file.type.startsWith('image/')) {
          toast.error('Please upload image files only');
          continue;
        }
        
        // Check file size and warn if large
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > 10) {
          toast.warning(`Large image detected (${fileSizeMB.toFixed(1)}MB). Compressing...`);
        }
        
        // Process with compression and progress tracking
        const compressedImage = await processImageFile(file, (progress) => {
          setImageProcessProgress(Math.round((i / files.length) * 100 + (progress / files.length)));
        });
        
        const compressedSize = getBase64Size(compressedImage);
        setCapturedImages(prev => [...prev, compressedImage]);
        
        toast.success(`Image added (${formatFileSize(compressedSize)})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.message?.includes('memory') || error.name === 'QuotaExceededError') {
        toast.error(
          'Unable to process image due to low memory. Try closing other tabs/apps and retry.',
          { duration: 5000, icon: <AlertTriangle className="w-5 h-5 text-yellow-500" /> }
        );
      } else {
        toast.error('Failed to process image. Please try a smaller image.');
      }
    } finally {
      setProcessingImage(false);
      setImageProcessProgress(0);
      // Reset file input
      if (e.target) e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  // New visual identification flow - Step 1: Identify candidates
  const submitForIdentification = async () => {
    if (capturedImages.length === 0) return;
    
    // Check guest limit (skip if user is logged in)
    if (!user && !checkActionLimit()) {
      return; // The limit overlay will be shown by GuestContext
    }
    
    setIdentificationStep('identifying');
    setScanProgress(10);
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setScanProgress(prev => Math.min(prev + 10, 85));
      }, 400);
      
      const response = await axios.post(`${API}/identify`, {
        image_base64: capturedImages[0],
        additional_images: capturedImages.slice(1)
      });
      
      clearInterval(progressInterval);
      setScanProgress(100);
      
      // Store identification result and move to selection step
      setIdentificationResult(response.data);
      setIdentificationStep('select');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to identify item';
      toast.error(message);
      setIdentificationStep('capture');
      setScanProgress(0);
    }
  };

  // New visual identification flow - Step 2: Confirm selection and get price
  const confirmCandidateSelection = async (selectedCandidate) => {
    setConfirmingSelection(true);
    setIdentificationStep('pricing');
    setScanProgress(10);
    
    try {
      const progressInterval = setInterval(() => {
        setScanProgress(prev => Math.min(prev + 15, 85));
      }, 400);
      
      const response = await axios.post(`${API}/scans/confirmed`, {
        image_base64: capturedImages[0],
        confirmed_item: selectedCandidate,
        condition: selectedCondition
      });
      
      clearInterval(progressInterval);
      setScanProgress(100);
      
      // Record action for guests
      if (!user) {
        recordAction();
        addToLocalHistory(response.data);
      }
      
      if (user) {
        await refreshUser();
      }
      
      toast.success('Item priced successfully!');
      
      setTimeout(() => {
        resetModalState();
        navigate(`/search/${response.data.id}`);
      }, 500);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to get pricing';
      toast.error(message);
      setIdentificationStep('select');
      setScanProgress(0);
    } finally {
      setConfirmingSelection(false);
    }
  };

  // Legacy direct scan (fallback for quick scan)
  const submitScan = async () => {
    if (capturedImages.length === 0) return;
    
    // Check guest limit (skip if user is logged in)
    if (!user && !checkActionLimit()) {
      return; // The limit overlay will be shown by GuestContext
    }
    
    setScanning(true);
    setScanProgress(10);
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setScanProgress(prev => Math.min(prev + 15, 85));
      }, 500);
      
      const response = await axios.post(`${API}/scans`, {
        image_base64: capturedImages[0], // Primary image
        additional_images: capturedImages.slice(1), // Additional images
        condition: selectedCondition
      });
      
      clearInterval(progressInterval);
      setScanProgress(100);
      
      // Record action for guests
      if (!user) {
        recordAction();
        addToLocalHistory(response.data);
      }
      
      if (user) {
        await refreshUser();
      }
      
      toast.success('Item identified successfully!');
      
      setTimeout(() => {
        resetModalState();
        navigate(`/search/${response.data.id}`);
      }, 500);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to analyze item';
      toast.error(message);
      setScanning(false);
      setScanProgress(0);
    }
  };

  const deleteScan = async (scanId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/scans/${scanId}`);
      setScans(scans.filter(s => s.id !== scanId));
      await refreshUser();
      toast.success('Search deleted');
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  // Reset all modal state
  const resetModalState = () => {
    stopCamera();
    setCapturedImages([]);
    setScanning(false);
    setScanProgress(0);
    setSelectedCondition('New');
    setIdentificationStep('capture');
    setIdentificationResult(null);
    setConfirmingSelection(false);
    setScanModalOpen(false);
  };

  const closeModal = () => {
    resetModalState();
  };

  // Go back to capture step from selection
  const goBackToCapture = () => {
    setIdentificationStep('capture');
    setIdentificationResult(null);
    setScanProgress(0);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const scansRemaining = user ? user.max_scans - user.scan_count : 999;

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border px-4 py-3">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <FlipValueLogoBox size="md" />
              <div className="text-left">
                <h1 className="font-bold text-base leading-tight">FlipValue</h1>
                <p className="text-[10px] text-muted-foreground -mt-0.5">
                  {isGuest ? 'Guest Mode' : 
                   user?.subscription_tier === 'lifetime_pro' ? 'Lifetime Pro' :
                   user?.subscription_tier === 'pro' ? 'Pro Plan' :
                   user?.subscription_tier === 'basic' ? 'Basic Plan' :
                   'Free Plan'}
                </p>
              </div>
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 py-6">
        {/* Welcome Section */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-1">Hey{user ? `, ${user.name?.split(' ')[0]}` : ''}!</h2>
          <p className="text-muted-foreground">Ready to find your next flip?</p>
        </div>

        {/* Search Bar - Primary feature */}
        <div className="mb-4">
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              if (!searchQuery.trim()) return;
              
              // Check if searching for cards and suggest Used condition
              if (isCardSearch(searchQuery) && searchCondition === 'New') {
                setShowCardSuggestion(true);
                return;
              }
              
              // Execute search and show confirmation
              executeSearch();
            }}
            className="space-y-2"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search any item (e.g., PSA 10 Charizard, Nike Dunk)"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowCardSuggestion(false); // Reset suggestion when typing
                }}
                onFocus={(e) => {
                  // Mobile keyboard fix - scroll input into view
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
                className="pl-12 h-12 rounded-xl"
                data-testid="search-input"
              />
            </div>
            
            {/* Card Suggestion Banner */}
            {showCardSuggestion && (
              <div className="flex items-center justify-between gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  💡 Trading cards are typically sold as Used. Search with Used condition?
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowCardSuggestion(false);
                      // Execute search with current (New) condition
                      executeSearch();
                    }}
                    className="text-xs"
                  >
                    Keep New
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      setShowCardSuggestion(false);
                      setSearchCondition('Used');
                      // Execute search directly with Used condition
                      setSearching(true);
                      try {
                        const response = await axios.post(`${API}/search`, { 
                          query: searchQuery.trim(),
                          condition: 'Used'
                        });
                        setSearchResults(response.data);
                        setShowConfirmResults(true);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Search failed');
                      } finally {
                        setSearching(false);
                      }
                    }}
                    className="text-xs"
                  >
                    Used
                  </Button>
                </div>
              </div>
            )}
            
            {/* Condition Toggle + Search Button Row */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1 bg-secondary rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => setSearchCondition('New')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    searchCondition === 'New' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  New
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchCondition('Used');
                    setShowCardSuggestion(false);
                  }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    searchCondition === 'Used' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Used
                </button>
              </div>
              
              <Button 
                type="submit" 
                variant="default"
                className="h-10 px-6 rounded-xl"
                disabled={searching || !searchQuery.trim()}
                data-testid="search-btn"
              >
                {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </form>
        </div>

        {/* Scan Button - Only show if enabled */}
        {ENABLE_SCAN && (
          <>
            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border"></div>
              <span className="text-sm text-muted-foreground">or upload a photo</span>
              <div className="flex-1 h-px bg-border"></div>
            </div>

            {/* Scan Button */}
            <div className="mb-8">
              <Button
                onClick={() => setScanModalOpen(true)}
                className="w-full h-14 rounded-2xl text-base font-semibold"
                data-testid="search-item-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Scan with Camera / Upload Photo
              </Button>
            </div>
          </>
        )}

        {/* Add spacing if scan is disabled */}
        {!ENABLE_SCAN && <div className="mb-4" />}

        {/* Quick Access Shortcuts */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => navigate('/inventory')}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-sm transition-all"
              data-testid="shortcut-inventory"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-500" />
              </div>
              <span className="text-xs font-medium">Inventory</span>
            </button>
            <button
              onClick={() => navigate('/calculator')}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-sm transition-all"
              data-testid="shortcut-calculator"
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-emerald-500" />
              </div>
              <span className="text-xs font-medium">Calculator</span>
            </button>
            <button
              onClick={() => navigate('/releases')}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-sm transition-all"
              data-testid="shortcut-releases"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-xs font-medium">Releases</span>
            </button>
          </div>
        </div>

        {/* Search History */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2">
              <History className="w-5 h-5" />
              Recent Searches
            </h3>
            <button 
              onClick={fetchScans}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="refresh-searches"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 rounded-xl skeleton" />
              ))}
            </div>
          ) : (isGuest ? localHistory : scans).length === 0 ? (
            <div className="text-center py-12 px-4 rounded-xl border border-dashed border-border">
              <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No searches yet</p>
              <p className="text-sm text-muted-foreground">Search for an item to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(isGuest ? localHistory.slice(0, 5) : scans.slice(0, 5)).map(scan => (
                <div
                  key={scan.id}
                  onClick={() => {
                    navigate(`/search/${scan.id}`);
                    window.scrollTo(0, 0);
                  }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:shadow-md transition-all cursor-pointer group"
                  data-testid={`search-item-${scan.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                    {scan.comps?.[0]?.image_url ? (
                      <img 
                        src={scan.comps[0].image_url} 
                        alt={scan.item.model}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {scan.item.brand && !scan.item.model?.toLowerCase().startsWith(scan.item.brand?.toLowerCase()) 
                        ? `${scan.item.brand} ${scan.item.model}` 
                        : scan.item.model}
                    </p>
                    <p className="text-xs text-muted-foreground">{scan.item.category}</p>
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold font-mono text-primary text-sm">${scan.average_price}</p>
                  </div>
                  
                  <button
                    onClick={(e) => deleteScan(scan.id, e)}
                    className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                    data-testid={`delete-search-${scan.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Search Results Confirmation Dialog */}
      <Dialog open={showConfirmResults} onOpenChange={setShowConfirmResults}>
        <DialogContent className="sm:max-w-sm p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Results for &ldquo;{searchQuery}&rdquo;</DialogTitle>
            <p className="text-[10px] text-muted-foreground">Add brand, model, size for better results</p>
          </DialogHeader>
          <div className="space-y-2">
            {searchResults?.comps?.length > 0 ? (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {searchResults.comps.slice(0, 5).map((comp, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-1.5 rounded border border-border bg-card">
                    {comp.image_url && (
                      <img src={comp.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] line-clamp-2">{comp.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No results found</p>
            )}
            
            <div className="bg-primary/10 rounded-lg p-2">
              <p className="text-[10px] text-muted-foreground">Estimated Value</p>
              <p className={`text-xl font-bold text-primary ${isFreeOrBasic ? 'blur-sm select-none' : ''}`}>
                ${searchResults?.average_price?.toFixed(2) || '0.00'}
              </p>
              {isFreeOrBasic && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {isGuest ? 'Sign up to see values' : 'Upgrade to see values'}
                </p>
              )}
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowConfirmResults(false)} className="flex-1" size="sm">
                Try Again
              </Button>
              <Button onClick={confirmResults} disabled={!searchResults?.comps?.length} className="flex-1" size="sm">
                Looks Good
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scan Modal */}
      <Dialog open={scanModalOpen} onOpenChange={(open) => {
        // Don't close if preview image is open
        if (!open && previewImage) return;
        if (!open) closeModal();
      }}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              {identificationStep === 'select' && (
                <button 
                  onClick={goBackToCapture}
                  className="p-1 rounded-lg hover:bg-secondary transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              {identificationStep === 'capture' && 'Search Item'}
              {identificationStep === 'identifying' && 'Identifying...'}
              {identificationStep === 'select' && 'Confirm Item'}
              {identificationStep === 'pricing' && 'Getting Price...'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            {/* Image Processing Indicator */}
            {processingImage && (
              <div className="py-8 text-center">
                <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary mb-3" />
                <p className="font-medium mb-2">Processing image...</p>
                <p className="text-sm text-muted-foreground mb-3">
                  Compressing for optimal quality
                </p>
                <Progress value={imageProcessProgress} className="h-2 max-w-xs mx-auto" />
              </div>
            )}
            
            {/* Step: Identifying */}
            {identificationStep === 'identifying' && (
              <div className="py-12 text-center">
                <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
                <p className="font-medium mb-2">Analyzing {capturedImages.length} image{capturedImages.length > 1 ? 's' : ''}...</p>
                <p className="text-sm text-muted-foreground mb-4">
                  AI is identifying your item
                </p>
                <Progress value={scanProgress} className="h-2" />
              </div>
            )}
            
            {/* Step: Select Candidate */}
            {identificationStep === 'select' && identificationResult && (
              <div>
                {/* Preview of captured image */}
                <div className="mb-4 flex justify-center">
                  <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-border">
                    <img 
                      src={capturedImages[0]} 
                      alt="Your item" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                {/* Condition Selector */}
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2 text-center">Item Condition</p>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => setSelectedCondition('New')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        selectedCondition === 'New' 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <Package className="w-4 h-4" />
                      <span className="font-medium text-sm">New</span>
                    </button>
                    <button
                      onClick={() => setSelectedCondition('Used')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        selectedCondition === 'Used' 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <PackageOpen className="w-4 h-4" />
                      <span className="font-medium text-sm">Used</span>
                    </button>
                  </div>
                </div>
                
                <CandidateSelector 
                  candidates={identificationResult.candidates}
                  imageQualityScore={identificationResult.image_quality_score}
                  suggestions={identificationResult.suggestions}
                  onSelect={confirmCandidateSelection}
                  onManualSearch={() => {
                    closeModal();
                    // Pre-fill search with best guess
                    if (identificationResult.candidates?.[0]) {
                      const c = identificationResult.candidates[0];
                      setSearchQuery(`${c.brand} ${c.model}`);
                    }
                  }}
                  loading={confirmingSelection}
                />
              </div>
            )}
            
            {/* Step: Getting Pricing */}
            {identificationStep === 'pricing' && (
              <div className="py-12 text-center">
                <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
                <p className="font-medium mb-2">Getting market prices...</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Searching eBay for comparable listings
                </p>
                <Progress value={scanProgress} className="h-2" />
              </div>
            )}
            
            {/* Legacy scanning state (fallback) */}
            {scanning && identificationStep === 'capture' && (
              <div className="py-12 text-center">
                <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
                <p className="font-medium mb-2">Analyzing {capturedImages.length} image{capturedImages.length > 1 ? 's' : ''}...</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Identifying brand, model & fetching prices
                </p>
                <Progress value={scanProgress} className="h-2" />
              </div>
            )}
            
            {/* Step: Capture - Show images */}
            {identificationStep === 'capture' && !processingImage && !scanning && capturedImages.length > 0 && (
              <div>
                {/* Image Gallery - Larger thumbnails */}
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    {capturedImages.map((img, index) => (
                      <div key={index} className="relative aspect-square rounded-xl overflow-hidden border-2 border-border">
                        <img 
                          src={img} 
                          alt={`Captured ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                        {/* Delete button - squared */}
                        <button
                          type="button"
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeImage(index);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeImage(index);
                          }}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-md flex items-center justify-center text-white hover:bg-red-600 active:bg-red-700 z-20 shadow-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                        {/* Expand button - squared */}
                        <button
                          type="button"
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPreviewImage(img);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPreviewImage(img);
                          }}
                          className="absolute top-2 left-2 w-8 h-8 bg-black/70 rounded-md flex items-center justify-center text-white hover:bg-black/90 active:bg-black z-20 shadow-lg"
                        >
                          <Maximize2 className="w-4 h-4" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-2 left-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-md font-medium">
                            Main
                          </span>
                        )}
                      </div>
                    ))}
                    {/* Add more buttons - separate buttons for camera and upload */}
                    {capturedImages.length < 5 && (
                      <button
                        type="button"
                        className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary active:bg-muted transition-colors"
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <Camera className="w-8 h-8 mb-2" />
                        <span className="text-sm font-medium">Camera</span>
                      </button>
                    )}
                    {capturedImages.length < 5 && (
                      <button
                        type="button"
                        className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary active:bg-muted transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-8 h-8 mb-2" />
                        <span className="text-sm font-medium">Upload</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {capturedImages.length}/5 images • First image is primary
                  </p>
                </div>
                
                {/* Condition Selector */}
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Item Condition</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedCondition('New')}
                      className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                        selectedCondition === 'New' 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      data-testid="condition-new"
                    >
                      <Package className="w-5 h-5" />
                      <span className="font-medium">New</span>
                    </button>
                    <button
                      onClick={() => setSelectedCondition('Used')}
                      className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                        selectedCondition === 'Used' 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      data-testid="condition-used"
                    >
                      <PackageOpen className="w-5 h-5" />
                      <span className="font-medium">Used</span>
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={submitScan}
                    className="flex-1"
                    data-testid="analyze-btn"
                  >
                    Get Price
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCapturedImages([])}
                    className="flex-1"
                    data-testid="retake-btn"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            )}
            
            {/* Camera Mode */}
            {identificationStep === 'capture' && !processingImage && !scanning && capturedImages.length === 0 && cameraMode && (
              <div>
                {/* Tip Banner */}
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-xs text-primary font-medium">
                    📸 Tip: Include brand tags, labels, or barcodes for better identification!
                  </p>
                </div>
                
                <div className="relative rounded-xl overflow-hidden mb-4 bg-black min-h-[300px] flex items-center justify-center">
                  <video 
                    ref={videoRef}
                    autoPlay 
                    playsInline
                    muted
                    className="w-full aspect-square object-cover"
                    onCanPlay={(e) => e.target.play()}
                  />
                  {!streamRef.current && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  )}
                  <div className="absolute inset-0 viewfinder pointer-events-none" />
                </div>
                
                {/* Captured count */}
                {capturedImages.length > 0 && (
                  <p className="text-sm text-center text-muted-foreground mb-3">
                    {capturedImages.length} photo{capturedImages.length > 1 ? 's' : ''} captured
                  </p>
                )}
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={stopCamera}
                    className="flex-1"
                    data-testid="cancel-camera-btn"
                    disabled={processingImage}
                  >
                    {capturedImages.length > 0 ? 'Done' : 'Cancel'}
                  </Button>
                  <Button
                    onClick={capturePhoto}
                    className="flex-1"
                    data-testid="capture-btn"
                    disabled={processingImage}
                  >
                    {processingImage ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-5 h-5 mr-2" />
                        Capture
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Initial state - No images captured yet */}
            {identificationStep === 'capture' && !processingImage && !scanning && capturedImages.length === 0 && !cameraMode && (
              <div className="space-y-4">
                {/* Tip Banner */}
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                  <p className="text-base font-semibold text-emerald-600 dark:text-emerald-500 mb-1">📸 Pro Tips for Better Results:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Include brand tags or labels in your photos</li>
                    <li>• Capture barcodes or UPC codes if visible</li>
                    <li>• Take multiple angles for better identification</li>
                  </ul>
                </div>
                
                <Button
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full h-14"
                  data-testid="open-camera-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Photos
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-background px-3 text-sm text-muted-foreground">or</span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-14"
                  data-testid="upload-photo-btn"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Photos
                </Button>
              </div>
            )}
          </div>
          
          {/* File input for gallery upload - always rendered */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            data-testid="file-input"
          />
          
          {/* Camera input for native camera capture */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="camera-input"
          />
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal - Blocks all interaction behind it */}
      {previewImage && (
        <>
          {/* Invisible blocker to capture all clicks */}
          <div 
            className="fixed inset-0"
            style={{ 
              zIndex: 10000,
              backgroundColor: 'rgba(0,0,0,0.95)',
              pointerEvents: 'auto'
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          {/* Image container */}
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 10001, pointerEvents: 'none' }}
          >
            <div 
              className="relative"
              style={{ pointerEvents: 'auto' }}
            >
              {/* Image - clicking it will minimize */}
              <img
                src={previewImage}
                alt="Preview"
                className="max-w-full max-h-[85vh] object-contain rounded-lg cursor-pointer"
                style={{ maxWidth: '90vw' }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreviewImage(null);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreviewImage(null);
                }}
              />
              {/* Minimize button - positioned at top right of image */}
              <button
                type="button"
                className="absolute top-2 right-2 px-3 py-2 bg-white hover:bg-gray-100 rounded-lg flex items-center justify-center text-black gap-2 font-medium shadow-lg"
                style={{ pointerEvents: 'auto' }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreviewImage(null);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreviewImage(null);
                }}
              >
                <Minimize2 className="w-4 h-4" />
                <span className="text-sm">Minimize</span>
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Onboarding Modal for new users */}
      <OnboardingModal />
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
